import { t } from '@/i18n'
import { useConfigStore } from '@/store/config'
import type { Printer } from '@/types/config'

import type { TicketElement } from './components'
import {
  Box,
  DottedSeparator,
  DoubleLabel,
  EmptySeparator,
  If,
  InfoItem,
  KitchenProduct,
  Label,
  Section,
  Separator,
  Title
} from './components'
import type {
  KitchenComboFormatted,
  KitchenOrderFormatted,
  KitchenProductFormatted
} from './kitchenOrderFormatter'

function isKitchenCombo(
  product: KitchenProductFormatted | KitchenComboFormatted
): product is KitchenComboFormatted {
  return 'comboId' in product && !!product.comboId
}

function pickupTypeLabel(type?: 'delivery' | 'ownDelivery' | 'takeAway') {
  if (!type) return null
  switch (type) {
    case 'delivery':
      return t('bill.external-delivery')
    case 'ownDelivery':
      return t('bill.own-delivery')
    case 'takeAway':
      return t('bill.take-away')
    default:
      return 'restaurant'
  }
}

function products(order: KitchenOrderFormatted) {
  return order.courses.flatMap(course => [
    new Section(course.course),
    ...course.products.flatMap(product => {
      if (isKitchenCombo(product)) {
        for (let i = 1; i < product.products.length; i++) {
          if (product.products[i].categoryPosition != product.products[i - 1].categoryPosition)
            product.products[i].separator = true
        }

        return [
          new EmptySeparator({ lineHeight: 10 }),
          new Label((product.comboName || '').toUpperCase(), {
            baseline: 'bottom',
            lineHeight: 30,
            style: 'bold 18px'
          }),
          ...product.products.flatMap(comboProduct => {
            let res: TicketElement[] = [new KitchenProduct(comboProduct, order.pickupType!)]
            if (comboProduct.separator)
              res = [new DottedSeparator({ size: 20, padding: 0 }), ...res]
            return res
          })
        ]
      } else {
        return [new KitchenProduct(product, order.pickupType!)]
      }
    })
  ])
}

function generatePaperTicket(order: KitchenOrderFormatted) {
  let kitchenOrderTitle
  if (order.pickupType) {
    kitchenOrderTitle = order.code || order.tabName
  } else {
    kitchenOrderTitle = order.tabName || order.code
  }
  const config = useConfigStore()
  const enabledBrands = config.virtualBrands.filter(virtualBrand => virtualBrand.enabled)
  return [
    new EmptySeparator({ lineHeight: 75 }),
    new If(order.modifiedOrder, new Box(t('kitchen-order.modified-order'))),
    new If(order.modifiedOrder, new EmptySeparator()),
    new Label(order.printerName?.toUpperCase(), {
      align: 'center',
      style: '25px',
      lineHeight: 30
    }),
    new Label(order.source?.toUpperCase(), {
      align: 'center',
      style: '25px'
    }),
    new If(kitchenOrderTitle, new Title(kitchenOrderTitle, { height: 90, fontSize: 60 })),
    new If(!kitchenOrderTitle, new EmptySeparator()),
    new If(
      enabledBrands?.length > 1,
      new Label(order.virtualBrandName?.toUpperCase(), {
        style: 'bold 20px',
        lineHeight: 25
      })
    ),
    new If(enabledBrands?.length > 1, new EmptySeparator()),
    new If(
      order.pickupType,
      new InfoItem(order.source, order.tabName, {
        important: true,
        fontSize: 20
      })
    ),
    new Label(mapKitchenOrderTime(order), {
      style: '18px',
      lineHeight: 30,
      valueType: 'date'
    }),
    new InfoItem(t('kitchen-order.person-in-charge-name'), order.waiterName),
    new If(order.seats, new InfoItem(t('kitchen-order.diners'), order.seats)),
    new InfoItem(t('kitchen-order.courier-name'), order.courierName),
    new InfoItem(t('kitchen-order.customer-name'), order.customerName, {
      important: true
    }),
    new If(
      order.pickupTime,
      new InfoItem(t('kitchen-order.pickup'), order.pickupTime, {
        valueType: 'date'
      })
    ),
    new If(
      order.estimatedDeliveryTime,
      new InfoItem(t('kitchen-order.delivery-time'), order.estimatedDeliveryTime, {
        valueType: 'date'
      })
    ),
    new If(order.pickupType, new Box(pickupTypeLabel(order.pickupType!)!)),
    ...products(order),
    new If(order.note, new Box(t('kitchen-order.note'), order.note!)),
    new If(order.allergyInfo, new Box(t('kitchen-order.allergies'), order.allergyInfo!)),
    new EmptySeparator(),
    new EmptySeparator()
  ]
}

function generateStickerTicket(order: KitchenOrderFormatted) {
  let tabCode = order.code
  if (order.tableName) tabCode += ` - ${order.tableName}`
  else if (order.tabName) tabCode += ` - ${order.tabName}`

  if (order.allergyInfo || order.note) tabCode += '*'
  return [
    new EmptySeparator(),
    new DoubleLabel(order.stickerPart, tabCode, {
      firstStyle: '27px Nunito Sans',
      secondStyle: 'bold 30px Nunito Sans'
    }),
    new Separator({ size: 4, padding: 10 }),
    new EmptySeparator(),
    ...order.courses.flatMap(course => [
      ...course.products.flatMap(product => {
        if (isKitchenCombo(product)) {
          return product.products.map(
            comboProduct =>
              new KitchenProduct(
                { ...comboProduct, name: `(C) ${comboProduct.name}` },
                order.pickupType!,
                {
                  showQuantity: false,
                  fontSize: 30
                }
              )
          )
        } else {
          return [
            new KitchenProduct(product, order.pickupType!, {
              showQuantity: false,
              fontSize: 30
            })
          ]
        }
      })
    ])
  ]
}

function generateKitchenOrder(order: KitchenOrderFormatted, printer: Printer) {
  if (printer.type === 'ZPL') {
    return generateStickerTicket(order)
  } else {
    return generatePaperTicket(order)
  }
}

export default generateKitchenOrder

function mapKitchenOrderTime(order: KitchenOrderFormatted) {
  if (order.versions?.length > 0) {
    const numVersions = order.versions.length
    return order.versions[numVersions - 1].sentTime
  }
  return order.activationTime || order.creationTime
}
